"use client";

import { keepPreviousData, useMutation, useQuery, useQueryClient, type Query } from "@tanstack/react-query";

import { showToast } from "@/components/ui/sonner";
import { ApiError, getApiErrorMessage } from "@/lib/api/error";
import {
  addJobScorecardDefinitionFromLibrary,
  createJobScorecardDefinition,
  customizeJobScorecardDefinitions,
  getListJobScorecardDefinitionsQueryKey,
  getListScorecardLibraryQueryKey,
  listJobScorecardDefinitions,
  reorderJobScorecardDefinitions,
  updateJobScorecardDefinition,
} from "@/lib/api/generated";
import type {
  ResolvedScorecardSet,
  ScorecardDefinitionCreate,
  ScorecardDefinitionFromLibrary,
  ScorecardDefinitionOut,
  ScorecardDefinitionPatch,
  ScorecardOrderUpdate,
} from "@/lib/api/model";

/** User-facing copy from the `ApiError` `customInstance` throws on non-2xx. */
export function apiErrorMessage(err: unknown): string {
  return getApiErrorMessage(err, "Something went wrong");
}

/** Backend `data.code`, mapped onto `ApiError.code` (also on `err.detail.code`). */
export function apiErrorCode(err: unknown): string | undefined {
  return err instanceof ApiError ? err.code : undefined;
}

/** Placeholder interpolated by the orval key factory so we can slice prefix/suffix. */
export const JOB_DEFINITIONS_KEY_PLACEHOLDER = "__jobId__";

export function jobScorecardDefinitionsKeyBounds(
  sample: string,
  placeholder: string = JOB_DEFINITIONS_KEY_PLACEHOLDER,
): { prefix: string; suffix: string } {
  const idx = sample.indexOf(placeholder);
  if (idx >= 0) {
    return { prefix: sample.slice(0, idx), suffix: sample.slice(idx + placeholder.length) };
  }
  return { prefix: "/api/jobs/", suffix: "/scorecard-definitions" };
}

/** Matches every job's scorecard-definition list query (any jobId / params). */
export function jobScorecardDefinitionsQueryFilter() {
  const sample = String(getListJobScorecardDefinitionsQueryKey(JOB_DEFINITIONS_KEY_PLACEHOLDER)[0]);
  const { prefix, suffix } = jobScorecardDefinitionsKeyBounds(sample);
  return {
    predicate: (query: Query) => {
      const head = query.queryKey[0];
      return typeof head === "string" && head.startsWith(prefix) && head.endsWith(suffix);
    },
  };
}

export function useJobScorecardDefinitions(jobId: string, includeArchived = false) {
  return useQuery({
    queryKey: getListJobScorecardDefinitionsQueryKey(jobId, {
      include_archived: includeArchived,
    }),
    queryFn: () => listJobScorecardDefinitions(jobId, { include_archived: includeArchived }),
    enabled: Boolean(jobId),
    placeholderData: keepPreviousData,
  });
}

function useInvalidateJob(jobId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: getListJobScorecardDefinitionsQueryKey(jobId) });
    void qc.invalidateQueries({ queryKey: getListScorecardLibraryQueryKey() });
  };
}

export function useCustomizeDefinitions(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useMutation({
    mutationFn: () => customizeJobScorecardDefinitions(jobId),
    onSuccess: () => {
      invalidate();
      showToast.success("Default set copied to this job");
    },
    onError: (err) => showToast.error("Could not customize", { description: apiErrorMessage(err) }),
  });
}

export function useCreateDefinition(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useMutation({
    mutationFn: (body: ScorecardDefinitionCreate) => createJobScorecardDefinition(jobId, body),
    onSuccess: () => {
      invalidate();
      showToast.success("Scorecard type created");
    },
    onError: (err) =>
      showToast.error("Could not create scorecard type", { description: apiErrorMessage(err) }),
  });
}

export function useAddFromLibrary(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useMutation({
    mutationFn: (body: ScorecardDefinitionFromLibrary) =>
      addJobScorecardDefinitionFromLibrary(jobId, body),
    onSuccess: () => {
      invalidate();
      showToast.success("Added from library");
    },
    onError: (err) =>
      showToast.error("Could not add from library", { description: apiErrorMessage(err) }),
  });
}

export function useUpdateDefinition(jobId: string) {
  const invalidate = useInvalidateJob(jobId);
  return useMutation({
    mutationFn: ({
      definitionId,
      body,
    }: {
      definitionId: string;
      body: ScorecardDefinitionPatch;
      successMessage?: string;
    }) => updateJobScorecardDefinition(jobId, definitionId, body),
    onSuccess: (_data, vars) => {
      invalidate();
      showToast.success(vars.successMessage ?? "Scorecard type updated");
    },
    onError: (err) =>
      showToast.error("Could not update scorecard type", { description: apiErrorMessage(err) }),
  });
}

export function useReorderDefinitions(jobId: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidateJob(jobId);
  return useMutation({
    mutationFn: (body: ScorecardOrderUpdate) => reorderJobScorecardDefinitions(jobId, body),
    onMutate: async (body) => {
      const key = getListJobScorecardDefinitionsQueryKey(jobId);
      await qc.cancelQueries({ queryKey: key });
      const snapshot = qc.getQueriesData<ResolvedScorecardSet>({ queryKey: key });
      qc.setQueriesData<ResolvedScorecardSet>({ queryKey: key }, (old) => {
        if (!old) return old;
        const byId = new Map(old.definitions.map((d) => [d.id, d]));
        const next = body.ids
          .map((id) => byId.get(id))
          .filter((d): d is ScorecardDefinitionOut => Boolean(d));
        const rest = old.definitions.filter((d) => !body.ids.includes(d.id));
        return { ...old, definitions: [...next, ...rest] };
      });
      return { snapshot };
    },
    onError: (err, _vars, context) => {
      if (context?.snapshot) {
        for (const [queryKey, data] of context.snapshot) {
          qc.setQueryData(queryKey, data);
        }
      }
      showToast.error("Could not reorder", { description: apiErrorMessage(err) });
    },
    onSettled: invalidate,
  });
}
